import { defineStore } from "pinia";
import { api } from "@/helpers/api.js";
import { startDownload } from "../helpers/download";
import { useUserStore } from "@/store/user";
import { isPriorityShop } from "@/brandconfig";

export const useBookingsStore = defineStore("bookings", {
  state: () => ({
    bookings: [],
    bookingorders: [],
    bookingorder_filter_bookings: [],
    pager: {
      page: 1,
      page_size: 25,
      total_size: 0,
      total_pages: 0,
    },
    query: {
      sorting: "created:desc",
    },
    openedTab: 0,
    bookingorder: null,
    memosPerBookingorder: null,
    guestdata: [],
    conditions: [],
    _events: null,
  }),
  getters: {
    events() {
      this.fetchBookedEvents();
      return this._events;
    },
    hasFilters() {
      const filters = this.query;
      return (
        filters.query || filters.event_startdate || filters.event_stopdate || filters.logistic_status || filters.event
      );
    },
  },
  actions: {
    async search() {
      const query = this.query;
      const pager = this.pager;

      const key = JSON.stringify({ query, pager });

      if (this.last === key) {
        return;
      } else {
        this.last = key;
      }

      const params = {
        sorting: query.sorting,
        page: pager.page,
        page_size: pager.page_size,
      };

      //We don't want to show all bookings when user is admin (for security reasons)
      if (useUserStore().isAdmin && !this.hasFilters) {
        this.bookingorders = [];
        this.pager = {
          page: 1,
          page_size: 25,
          total_size: 0,
          total_pages: 0,
        };

        return;
      }

      if (query.query) {
        params.query = "like:" + query.query;
      }

      if (query.event) {
        await this.fetchBookedEvents();
        const event = this.getBookedEvent(query.event);
        params.event_id = event.event_id;
      }

      if (query.event_startdate) {
        params.event_startdate = `ge:${query.event_startdate}`;
      }

      if (query.event_stopdate) {
        params.event_stopdate = `le:${query.event_stopdate}`;
      }

      if (query.logistic_status) {
        params.logistic_status = query.logistic_status;
      }

      params.mass_booking = false;
      const req = api.get("/bookingorders/list?" + new URLSearchParams(params));
      const res = await req;
      this.bookingorders = res.data.bookingorders;
      this.pager.total_size = res.data.pagination.total_size;
      this.pager.total_pages = Math.ceil(this.pager.total_size / this.pager.page_size);
    },

    async fetchBookedEvents() {
      if (!this.req) {
        const params = new URLSearchParams();
        params.append("booked", true);
        params.append("sorting", "date_stop:desc");

        this._events = [];
        this.req = api.cache("/events?" + params);

        const res = await this.req;
        this._events = res.data.events;
      }
      return this.req;
    },

    async fetchOrderItems() {
      await this.fetchBookedEvents();
      const slug = this.query.event;
      const event = this.getBookedEvent(slug);

      const params = {
        page_size: 9999,
        event_id: event.event_id,
      };

      const req = api.get("/bookingorders?" + new URLSearchParams(params));
      const res = await req;

      return res.data.bookingorders;
    },

    getBookedEvent(slug) {
      for (const event of this.events) {
        if (event.slug === slug) {
          return event;
        }
      }
    },

    setGuestData(items) {
      this.conditions = items.slice();
      this.guestdata = items.map((item) => {
        return {
          ...item,
          guests: item.guests.map((guest) => {
            const values = {};
            for (const k in guest) {
              values[k] = guest[k].value || "";
            }
            values.guest_id = guest.guest_id || undefined;
            values.lead_guest = guest.lead_guest;
            return values;
          }),
        };
      });
    },

    async fetchGuestData(bookingorderId) {
      this.guestdata = null;

      const req = api.get("/bookingorders/" + bookingorderId + "/guestdata?include_conditions=true");
      const res = await req;

      this.setGuestData(res.data.items);

      return res.data;
    },

    async updateGuestData(i, j, field, value) {
      this.guestdata[i].guests[j][field] = value;
    },
    openTab(index) {
      this.openedTab = index;
    },
    async storeGuestData(bookingorderId) {
      try {
        await api.put("/bookingorders/" + bookingorderId + "/guestdata", {
          items: this.guestdata,
        });
      } catch (err) {
        const res = err.response;
        this.setGuestData(res.data.items);
        throw err;
      }
      await this.fetchGuestData(bookingorderId);
    },

    updateSingleGuestData(bookingorderId, guest) {
      return api.put(`/bookingorders/${bookingorderId}/guestdata/${guest.guest_id}`, guest).then((res) => {
        return res;
      });
    },

    async fetchInvoice(bookingorderId, invoice) {
      const k = Date.now() & 0xffffffff;
      const endpoint = `/bookingorders/${bookingorderId}/invoice/${invoice}?key=${k}`;
      return await api.get(endpoint, { responseType: "arraybuffer" }).then((res) => {
        return window.URL.createObjectURL(new Blob([res.data]), { type: "application/pdf" });
      });
    },

    async fetchTickets(bookingorderId) {
      const k = Date.now() & 0xffffffff;
      const endpoint = `/etickets/download/zip/${bookingorderId}?key=${k}`;
      return await api.get(endpoint);
    },

    clear() {
      for (const k in this.query) {
        delete this.query[k];
      }
    },

    reset(query) {
      this.clear();
      Object.assign(this.query, query);
    },

    createMolliePayment(payload) {
      if (isPriorityShop) {
        if (localStorage.returnUrl) {
          payload.referer_url = localStorage.returnUrl;
        } else {
          payload.returnUrl = "myvfnl://gigapriorityinternalwebview/?";
        }
      }
      return api
        .post(`support/payment`, payload)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          throw error;
        });
    },

    bookingNotify(reservation_id) {
      return api
        .post(`bookings/${reservation_id}/notify-endcustomer`)
        .then(() => {})
        .catch((error) => {
          throw error;
        });
    },

    fetchBookingOnReservation(reservation_id) {
      return api
        .get(`bookings/reservation/${reservation_id}`)
        .then((res) => {
          this.bookings = res.data;
        })
        .catch((error) => {
          throw error;
        });
    },

    async fetchBookingOrder(bookingorderId) {
      const req = api.get("/bookingorders/" + bookingorderId);
      const res = await req;
      const bookingorder = res.data;
      this.bookingorder = bookingorder;
    },

    async fetchBookingsById(unique_booking_ids) {
      let getUri = "bookings";
      const bookingChunks = [];
      const bookings = [];
      const chunkSize = 50; // 50 * 36 characters is 1800 so were still under max url length of 2000

      for (let i = 0; i < unique_booking_ids.length; i += chunkSize) {
        let params = new URLSearchParams();
        const chunk = unique_booking_ids.slice(i, i + chunkSize);
        const unique_booking_id_chunk = { booking_ids: chunk };
        params.append("booking_id", `in:${unique_booking_id_chunk.booking_ids}`);
        bookingChunks.push(
          api.get(`${getUri}?` + params).then((response) => {
            bookings.push(...response.data.bookings);
          })
        );
      }
      Promise.all(bookingChunks).then(() => {
        this.bookingorder_filter_bookings = bookings;
      });
    },
    getBookingCode(id) {
      const booking = this.bookingorder_filter_bookings.find((i) => i.booking_id === id);
      if (booking) {
        return booking.booking_code;
      }
    },

    async getTicketsForBranded(bookingId) {
      const req = api.get(`bookingorders/restricted/${bookingId}`);
      const res = await req;
      return res.data;
    },

    downloadTicketsAsZip(payload) {
      const { bookingOrderId, fileName } = payload;
      let uri = `etickets/download/zip/${bookingOrderId}`;
      return api
        .get(uri)
        .then((res) => {
          startDownload(res.data, `tickets-${fileName.toLowerCase()}.zip`);
        })
        .catch((e) => {
          throw e;
        });
    },

    downloadSingleTicket(payload) {
      const { bookingorderId, orderItemId, url } = payload;
      if (url) {
        if (url.toLowerCase().endsWith("pkpass")) {
          const getUri = `etickets/download/pkpass/${bookingorderId}/${orderItemId}/url/${url}`;
          return api
            .get(getUri)
            .then(() => {
              startDownload(url);
            })
            .catch((e) => {
              throw e;
            });
        } else {
          const seconds = Math.floor(Date.now() / 1000);
          const getUri = `etickets/download/${bookingorderId}/${orderItemId}/url/${url}?key=${seconds}`;
          return api
            .get(getUri, { responseType: "blob" })
            .then((res) => {
              const url = window.URL.createObjectURL(res.data);
              startDownload(url, `ticket-${orderItemId}`);
            })
            .catch((e) => {
              throw e;
            });
        }
      }
    },

    fetchTicketFile(bookingorderId, orderItemId, urlName) {
      const seconds = Math.floor(Date.now() / 1000);
      const getUri = `etickets/download/${bookingorderId}/${orderItemId}/url/${urlName}?key=${seconds}`;
      return api.get(getUri, { responseType: "blob" }).then((res) => {
        const url = window.URL.createObjectURL(res.data);
        startDownload(url, `ticket-${urlName}`);
      });
    },
    async fetchBookingOrderExtras(payload) {
      const { bookingOrderId } = payload;
      let getUri = `bookingorders/${bookingOrderId}/extras`;
      const response = await api.get(`${getUri}`);
      return response.data.bookingorderextras;
    },
    async downloadBookingOrderExtra(payload) {
      const { bookingOrderId, bookingOrderExtraId } = payload;
      const getUri = `bookingorders/${bookingOrderId}/extras/${bookingOrderExtraId}/download`;
      const response = await api.get(getUri, { responseType: "arraybuffer" });
      return response.data;
    },

    async getMemosByBookingorderIds(uniqueBookingorderIds) {
      const memoChunks = [];
      const chunkSize = 50;
      const bookingMemoLookup = {};

      for (let i = 0; i < uniqueBookingorderIds.length; i += chunkSize) {
        let params = new URLSearchParams();
        const chunk = uniqueBookingorderIds.slice(i, i + chunkSize);
        params.append("bookingorder_id", `in:[${chunk}]`);
        // params.append("scope", `external`)
        memoChunks.push(
          api.get("memos", { params: params }).then((res) => {
            res.data.memos?.forEach((memo) => {
              let savedEntries = bookingMemoLookup[memo.bookingorder_id];
              if (savedEntries === undefined) {
                savedEntries = [memo];
              } else {
                savedEntries = [...savedEntries, memo];
              }
              bookingMemoLookup[memo.bookingorder_id] = savedEntries;
            });
          })
        );
      }
      Promise.all(memoChunks).then(() => {
        this.memosPerBookingorder = bookingMemoLookup;
      });
    },

    saveEmail(payload) {
      return api
        .post(`bookingorders/restricted/${payload.bookingorderId}/notify`, payload)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },

    setGuestDataXs2ticket(items) {
      this.conditions = items.map((item) => {
        return {
          ...item,
          guests: item.guests.map((guest) => {
            const values = {};
            for (const k in guest) {
              values[k] = guest[k].value || "";
            }
            values.guest_id = guest.guest_id || undefined;
            values.lead_guest = guest.lead_guest;
            return values;
          }),
        };
      });
    },

    async fetchBookingOrderGuestdata(bookingorderId) {
      const res = await api.get(`bookingorders/${bookingorderId}/guestdata?include_conditions=true`);
      this.setGuestDataXs2ticket(res.data.items);
      return res.data;
    },
  },
});
